import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios';
import {NotificationManager} from 'react-notifications';
import {API_ENDPOINT} from '../config';
import {useAuth} from '../auth/AuthContext';

const MAX_IMAGES = 3;

// Semafor pe zilele ramase: expirat -> rosu, sub o luna -> galben, altfel verde.
const expiryBadge = (daysLeft) => {
    if (daysLeft === null || daysLeft === undefined) {
        return {cls: 'badge badge-secondary', text: 'fara abonament'};
    }
    if (daysLeft < 0) {
        return {cls: 'badge badge-danger', text: `expirat (${Math.abs(daysLeft)} zile)`};
    }
    if (daysLeft <= 30) {
        return {cls: 'badge badge-warning', text: `${daysLeft} zile`};
    }
    return {cls: 'badge badge-success', text: `${daysLeft} zile`};
};

const statusBadge = (status) => {
    const map = {
        success: 'badge badge-success',
        failed: 'badge badge-danger',
        running: 'badge badge-info',
        queued: 'badge badge-secondary',
    };
    return map[status] || 'badge badge-light';
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('ro-RO') : '—');

const ReportCompanies = () => {
    const {auth} = useAuth();
    const [state, setState] = useState({loading: true, error: null, data: null});
    const [page, setPage] = useState(1);

    const load = useCallback(async (targetPage) => {
        setState((s) => ({...s, loading: true, error: null}));
        try {
            const {data} = await axios.get(`${API_ENDPOINT}/v1/admin/companies`, {
                headers: {Authorization: `Bearer ${auth.token}`},
                params: {page: targetPage, limit: 25},
            });
            setState({loading: false, error: null, data});
        } catch (e) {
            // Distingem "nu stiu" de "zero": altfel un raport gol arata ca o platforma goala.
            const msg = e?.response?.status === 403
                ? 'Nu ai drepturi pentru acest raport.'
                : 'Nu am putut incarca lista de companii.';
            setState({loading: false, error: msg, data: null});
            NotificationManager.error(msg);
        }
    }, [auth.token]);

    useEffect(() => {
        load(page);
    }, [load, page]);

    if (state.loading) {
        return <div className="card"><div className="card-body text-center text-muted">Se incarca…</div></div>;
    }

    if (state.error) {
        return (
            <div className="card">
                <div className="card-body">
                    <p className="text-danger mb-2" style={{wordBreak: 'break-word'}}>{state.error}</p>
                    <button className="btn btn-sm btn-outline-secondary" onClick={() => load(page)}>Reincearca</button>
                </div>
            </div>
        );
    }

    const {items, total, pages} = state.data;

    return (
        <div className="card">
            <div className="card-header">
                <h3 className="card-title">
                    {total} companii
                    {typeof state.data.totalSuccessfulGenerations === 'number' && (
                        <span className="badge badge-success ml-2">
                            {state.data.totalSuccessfulGenerations} generări reușite
                        </span>
                    )}
                </h3>
                <div className="card-tools text-muted" style={{fontSize: 12}}>
                    sortate dupa expirare
                </div>
            </div>
            <div className="card-body table-responsive p-0">
                <table className="table table-hover text-nowrap">
                    <thead>
                        <tr>
                            <th>Cod</th>
                            <th>Nume</th>
                            <th>Useri</th>
                            <th className="text-center">Imagini</th>
                            <th>Expira in</th>
                            <th>Ultima generare</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 && (
                            <tr><td colSpan="6" className="text-center text-muted">Nicio companie.</td></tr>
                        )}
                        {items.map((c) => {
                            const badge = expiryBadge(c.daysLeft);
                            return (
                                <tr key={c.id}>
                                    <td><code>{c.code}</code></td>
                                    <td style={{whiteSpace: 'normal', wordBreak: 'break-word'}}>
                                        {c.displayName ? <strong>{c.displayName}</strong> : c.name}
                                        {c.displayName && <div className="text-muted" style={{fontSize: 11}}>{c.name}</div>}
                                    </td>
                                    <td style={{whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 260}}>
                                        {c.usersEmails
                                            ? <>
                                                {c.usersEmails}
                                                {c.usersCount > 1 && (
                                                    <span className="badge badge-light ml-1">{c.usersCount}</span>
                                                )}
                                            </>
                                            : <span className="badge badge-warning">fara user</span>}
                                    </td>
                                    <td className="text-center">
                                        <span className={c.imagesCount >= MAX_IMAGES ? 'text-warning font-weight-bold' : ''}>
                                            {c.imagesCount}/{MAX_IMAGES}
                                        </span>
                                    </td>
                                    <td><span className={badge.cls}>{badge.text}</span></td>
                                    <td>
                                        {c.lastStatus
                                            ? <><span className={statusBadge(c.lastStatus)}>{c.lastStatus}</span>{' '}
                                                <span className="text-muted" style={{fontSize: 11}}>{formatDate(c.lastRequestedAt)}</span></>
                                            : <span className="text-muted">—</span>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {pages > 1 && (
                <div className="card-footer d-flex justify-content-between align-items-center">
                    <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1}
                            onClick={() => setPage((p) => p - 1)}>Anterior</button>
                    <span className="text-muted" style={{fontSize: 12}}>pagina {page} din {pages}</span>
                    <button className="btn btn-sm btn-outline-secondary" disabled={page >= pages}
                            onClick={() => setPage((p) => p + 1)}>Urmator</button>
                </div>
            )}
        </div>
    );
};

export default ReportCompanies;
